/**
 * LandmarkStrip — GLOBAL snippet (iter 240, hardened iter 361).
 *
 * Horizontal chip rail of local landmarks (parks, beaches, libraries,
 * museums, hospitals, universities, …) served from
 * ``GET /api/directory/landmarks`` which reads ``hustlegeo_master.hp_pois``.
 *
 * Location-aware: passes suburb / postcode / lat / lng straight through
 * so a Brisbane user never sees Bunbury WA. Falls back to nationwide only
 * when the user hasn't picked any location signals.
 *
 * Now shows LEFT/RIGHT scroll arrows so users know the rail is a
 * horizontal carousel (fixes iter 361 UX report).
 */
import { useEffect, useRef, useState } from "react";
import { MapPin, ChevronLeft, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

const KIND_ICON = {
  park: "🌳", beach: "🏖️", library: "📚", museum: "🏛️",
  hospital: "🏥", clinic: "🩺", veterinary: "🐾", university: "🎓",
  cinema: "🎬", theatre: "🎭", stadium: "🏟️", shopping_centre: "🛍️",
  market: "🛒", attraction: "✨", viewpoint: "🌄", monument: "🗿",
  art_gallery: "🖼️", toilet: "🚻", drinking_fountain: "🚰",
  playground: "🛝", dog_park: "🐕", swimming_pool: "🏊",
  sports_field: "⚽", caravan_park: "🚐", ev_charging: "⚡",
  fishing_bait: "🎣", restaurant: "🍽️",
};

function fmtDistance(m) {
  if (m == null) return null;
  if (m < 1000) return `${m}m away`;
  return `${(m / 1000).toFixed(1)}km away`;
}

function LandmarkChip({ item, onSelect }) {
  const icon = KIND_ICON[item.kind] || "📍";
  const parts = [];
  if (item.suburb) parts.push(item.suburb);
  if (item.state) parts.push(item.state);
  if (item.distance_m != null) parts.push(fmtDistance(item.distance_m));
  const subtitle = parts.join(" · ") || item.kind;
  return (
    <button
      type="button"
      data-testid={`landmark-chip-${item.slug || item.id}`}
      onClick={() => onSelect(item)}
      className="shrink-0 inline-flex items-center gap-2 pl-2.5 pr-3.5 py-1.5 rounded-full
                 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700
                 hover:border-emerald-500 dark:hover:border-emerald-400
                 hover:bg-emerald-50 dark:hover:bg-stone-700
                 text-stone-800 dark:text-stone-100 text-[12px] font-medium
                 transition-colors whitespace-nowrap cursor-pointer"
      title={subtitle}
    >
      <span className="text-base leading-none">{icon}</span>
      <span className="max-w-[14rem] truncate">{item.name}</span>
      {subtitle && (
        <span className="text-stone-500 dark:text-stone-400 text-[11px] max-w-[16rem] truncate">
          · {subtitle}
        </span>
      )}
    </button>
  );
}

export default function LandmarkStrip({
  district = null,
  state = null,
  suburb = null,
  postcode = null,
  lat = null,
  lng = null,
  kind = null,
  limit = 20,
  onSelect,
  testid = "landmark-strip",
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const railRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  useEffect(() => {
    const qs = new URLSearchParams();
    // Prefer explicit lat/lng — it always beats a stale district string.
    if (lat != null && lng != null) {
      qs.set("lat", String(lat));
      qs.set("lng", String(lng));
    }
    if (suburb) qs.set("suburb", suburb);
    else if (district) qs.set("district", district);
    if (postcode) qs.set("postcode", postcode);
    if (state)    qs.set("state", state);
    if (kind)     qs.set("kind", kind);
    qs.set("limit", String(limit));
    let cancelled = false;
    setLoading(true);
    fetch(`${API}/api/directory/landmarks?${qs.toString()}`)
      .then((r) => (r.ok ? r.json() : { landmarks: [] }))
      .then((d) => { if (!cancelled) setItems(d.landmarks || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [district, suburb, postcode, state, kind, limit, lat, lng]);

  // Toggle left/right arrow visibility based on rail scroll position.
  const updateArrows = () => {
    const el = railRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };
  useEffect(() => {
    updateArrows();
    const el = railRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [items]);
  const scrollByAmt = (dx) => {
    const el = railRef.current;
    if (!el) return;
    el.scrollBy({ left: dx, behavior: "smooth" });
  };

  const handleSelect = (item) => {
    // Fire intent beacon — same endpoint the finder + sales-page rail use.
    try {
      fetch(`${API}/api/finder/click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          landmark_slug:   item.slug,
          landmark_kind:   item.kind,
          landmark_source: "directory-strip",
          name:            item.name,
          lat:             item.lat,
          lng:             item.lng,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (_e) { /* ignore */ }
    if (onSelect) onSelect(item);
  };

  if (loading && items.length === 0) {
    return (
      <div data-testid={`${testid}-loading`} className="max-w-6xl mx-auto px-4 pt-4">
        <div className="h-8 rounded-full bg-stone-100 dark:bg-stone-800 animate-pulse w-2/3" />
      </div>
    );
  }
  if (!loading && items.length === 0) return null;

  return (
    <section
      data-testid={testid}
      className="max-w-6xl mx-auto px-4 pt-4"
      aria-label="Nearby landmarks"
    >
      <div className="flex items-center gap-2 mb-2">
        <MapPin className="w-3.5 h-3.5 text-emerald-700 dark:text-emerald-400" strokeWidth={2} />
        <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-stone-500 dark:text-stone-400">
          Landmarks nearby
        </p>
        <span className="text-[10px] text-stone-400 dark:text-stone-500 italic">
          swipe or use arrows →
        </span>
      </div>
      <div className="relative">
        {/* Fade gradients on the edges so it visually reads as a carousel */}
        {canLeft && (
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-stone-50/95 dark:from-stone-950/95 to-transparent z-10 rounded-l-lg" />
        )}
        {canRight && (
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-stone-50/95 dark:from-stone-950/95 to-transparent z-10 rounded-r-lg" />
        )}
        {/* Arrow buttons — always mounted so their position doesn't jump.
            Hidden when the rail can't scroll in that direction. */}
        {canLeft && (
          <button
            type="button"
            data-testid={`${testid}-arrow-left`}
            aria-label="Scroll left"
            onClick={() => scrollByAmt(-260)}
            className="absolute left-1 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full
                       bg-white/95 dark:bg-stone-800/95 border border-stone-200 dark:border-stone-700
                       shadow-md hover:bg-emerald-50 dark:hover:bg-stone-700
                       flex items-center justify-center transition-colors"
          >
            <ChevronLeft className="w-4 h-4 text-stone-700 dark:text-stone-100" />
          </button>
        )}
        {canRight && (
          <button
            type="button"
            data-testid={`${testid}-arrow-right`}
            aria-label="Scroll right"
            onClick={() => scrollByAmt(260)}
            className="absolute right-1 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full
                       bg-white/95 dark:bg-stone-800/95 border border-stone-200 dark:border-stone-700
                       shadow-md hover:bg-emerald-50 dark:hover:bg-stone-700
                       flex items-center justify-center transition-colors"
          >
            <ChevronRight className="w-4 h-4 text-stone-700 dark:text-stone-100" />
          </button>
        )}
        <div
          ref={railRef}
          className="flex gap-2 overflow-x-auto pb-2 no-scrollbar scroll-smooth"
          data-testid={`${testid}-rail`}
        >
          {items.map((item) => (
            <LandmarkChip key={item.id} item={item} onSelect={handleSelect} />
          ))}
        </div>
      </div>
    </section>
  );
}
